/**
 * Board tab: what the printed table board shows everyone (DESIGN §9.4).
 * Bed counts and shared kit only. No other seat's patients.
 */
import { bookingStatusLabel, type Affordance, type BoardView, type CoordinationView, type SeatView } from '@nightshift/engine/client';
import type { ReactElement } from 'react';
import { Fold, RESOURCE_TITLE, ROLE_TITLE } from '../ui/bits.tsx';
import { TheatreOverview } from './TheatreOverview.tsx';

/** Keep each service's physical capacity separate from proposed transfers. */
function ServiceBeds({ v }: { v: SeatView }): ReactElement {
  const b = v.board;
  return <>
    <section className="bd-seats" aria-label="Beds at this table">
        {b.seats.filter((raw) => raw.beds !== null).map((raw) => {
          // Only your own service shows counts, and they are live.
          const s = { ...raw, beds: raw.beds!, used: v.beds.filter((x) => x.patient).length, corridor: v.corridor.length };
          return (
          <div key={s.role} className={`bd-seat spec-${s.role} ${s.role === v.me.role ? 'me' : ''}`}>
            <span className="bd-seat-head">
              <span className="spec-dot" />
              <strong>{ROLE_TITLE[s.role]}</strong>
              {b.lead === s.role && <span className="lead-badge">Lead</span>}
            </span>
            <BedMeter used={s.used} beds={s.beds} corridor={s.corridor} />
            <span className="bd-seat-foot">
              {s.used} of {s.beds} beds
              {s.corridor > 0 && <em className="bad"> and {s.corridor} in the corridor</em>}
            </span>
            <span className="bd-seat-tags">
              {s.interrupts !== null && s.interrupts > 0 && <span className="tag">{s.interrupts} interruption{s.interrupts > 1 ? 's' : ''}</span>}
              {s.staff !== null && <span className="tag">{s.staff} Staff</span>}
              {s.bot && <span className="tag">bot</span>}
            </span>
          </div>
          );
        })}
      </section>
      {b.capacity && <section className="bd-seats" aria-label="Other departments">
        {b.capacity.map((c) => (
          <div key={c.role} className={`bd-seat spec-${c.role}`} data-testid={`capacity-${c.role}`}>
            <span className="bd-seat-head"><span className="spec-dot" /><strong>{ROLE_TITLE[c.role]}</strong></span>
            <span className="bd-seat-foot">{CAPACITY_WORDS[c.level]}</span>
          </div>
        ))}
      </section>}
  </>;
}

const CAPACITY_WORDS = { available: 'Available', delays: 'Delays', extreme: 'Extreme' } as const;

/** Show equipment that will be shared when saved plans are committed. */
function SharedKit({ b, proposals, role }: { b: BoardView; proposals: CoordinationView['allocations']; role: SeatView['me']['role'] }): ReactElement {
  const theatrePair = role === 'SURG' || role === 'ICU';
  return (
    <section className="bd-kit" aria-label="Shared kit">
        <h3 className="sec-title">Shared kit</h3>
        {proposals.length > 0 && <p className="muted small">
          <strong>Requests to keep shared kit</strong> · {`Current Lead: ${ROLE_TITLE[b.lead]}`}. Requests are shown in each player’s saved order.
          A change of Lead or other plans can change who gets it when the turn ends.
        </p>}
        <div className="kit-grid">
          {b.scanner.map((sc) => (
            <Kit key={sc.box} name={`Scanner ${sc.box + 1}`} state={sc.closed ? 'closed' : sc.usedBy ? 'used' : 'free'} detail={sc.closed ? 'closed this turn' : sc.usedBy ? `used by ${ROLE_TITLE[sc.usedBy]}` : sc.allocatedTo ? `kept for ${ROLE_TITLE[sc.allocatedTo]}` : 'open to everyone, highest priority first'}
              allocation={<AllocationPlans proposals={proposals} slot={sc.box === 0 ? 'scanner0' : 'scanner1'} />} />
          ))}
          <Kit
            name="Theatre"
            state={b.theatre.closed ? 'closed' : b.theatre.used ? 'used' : b.theatre.booked || b.theatre.anaesthetic ? 'busy' : 'free'}
            detail={
              b.theatre.closed
                ? b.theatre.closed
                : b.theatre.used
                  ? 'used this turn'
                  : !theatrePair
                    ? 'booking details are for the Surgeon and Intensivist'
                  : b.theatre.booked && !b.theatre.anaesthetic
                    ? 'Surgeon waiting for an anaesthetic'
                    : b.theatre.anaesthetic && !b.theatre.booked
                      ? 'Intensivist standing by'
                      : b.theatre.allocatedTo
                        ? `kept for ${ROLE_TITLE[b.theatre.allocatedTo]}`
                        : 'free'
            }
            allocation={<AllocationPlans proposals={proposals} slot="theatre" />}
          />
          {b.secondTheatre && <Kit name="Second Theatre" state={b.secondTheatre.closed ? 'closed' : b.secondTheatre.used ? 'used' : 'free'} detail={b.secondTheatre.closed ?? (b.secondTheatre.used ? 'used this turn' : 'anaesthetist included')} />}
          {b.icu && <Kit name="ICU" state={b.icu.occupied >= b.icu.beds ? 'used' : 'free'} detail={`${b.icu.occupied} of ${b.icu.beds} beds${b.icu.supportsNeeded > 1 ? `; ${b.icu.supportsNeeded} supports each` : ''}`} />}
          {b.vents && <Kit name="Ventilators" state={b.vents.free === 0 ? 'used' : 'free'} detail={`${b.vents.free} of ${b.vents.total} free`} />}
          {b.blood && <Kit name="Blood bank" state={b.blood.units === 0 ? 'used' : 'free'} detail={`${b.blood.units} unit${b.blood.units === 1 ? '' : 's'}${b.blood.ordered ? ', more ordered' : ''}`} />}
          {(b.locum || b.locumComing) && <Kit name="Locum" state="busy" detail={b.locum ? `${b.locum.actionsLeft} action${b.locum.actionsLeft === 1 ? '' : 's'} left` : 'see booked arrival turns above'} />}
        </div>
      </section>
  );
}

/** Keep tentative requests beside the equipment's current reservation. */
function AllocationPlans({ proposals, slot }: { proposals: CoordinationView['allocations']; slot: CoordinationView['allocations'][number]['slot'] }): ReactElement | null {
  const plans = proposals.filter(proposal => proposal.slot === slot);
  if (plans.length === 0) return null;
  return <ol className="kit-detail" data-testid={`allocation-${slot}`} aria-label="Requests to keep this kit">
    {plans.map((plan, index) => <li key={index}>
      <span>{`${ROLE_TITLE[plan.sender]} proposes: ${plan.to ? `keep for ${ROLE_TITLE[plan.to]}` : slot === 'theatre' ? 'share in table order' : 'open to everyone, highest priority first'}`}</span>
      {plan.currentReason && <p className="bad small">{`Currently: ${plan.currentReason}`}</p>}
    </li>)}
  </ol>;
}

/** Explain current excess capacity and the fixed turn-start delay risk. */
function Workload({ v }: { v: SeatView }): ReactElement {
  const w = v.workload;
  return <section className="bd-kit" aria-label="Department workload">
    <h3 className="sec-title">Department workload</h3>
    <p>{w.census} patients · {w.beds} beds · {w.bedExcess} more patients than beds</p>
    {w.staffAssigned !== null && <p>{w.staffAssigned} Staff assigned · {w.staffDemand} needed · {w.staffShortfall} short</p>}
    {w.exposure && <p data-testid="overflow-risk">This turn: {Math.round(w.exposure.probability * 100)}% chance that one patient ready to go home has to stay an extra turn.</p>}
    <p className="muted">Too few beds or too few staff both add to this risk. At most one patient per team is held back each turn, and each patient only once per shift. A held-back discharge costs no action; plan it again next turn.</p>
    <p className="muted">The risk is set at the start of the turn. A new referral counts for the receiving team from next turn. Beds, staff, equipment and actions are still separate limits.</p>
    {v.board.divert && <p className="phase-note">On divert: new arrivals can go to another table with room. If none has space, they stay here.</p>}
    {v.board.divertNotice.map(d => <p key={d.fromTable} className="phase-note">Table {d.fromTable} sent you {d.count} patient{d.count === 1 ? '' : 's'} this turn.</p>)}
  </section>;
}

export function BoardTab({ v, act }: { v: SeatView; act: (a: Affordance) => Promise<boolean> }) {
  const b = v.board;
  const layerActs = v.seatActions.filter((a) => a.group === 'layer');
  return (
    <div className="board">
      <Forecast forecast={b.forecast} />
      <TheatreOverview v={v} />
      <Bookings bookings={b.bookings} />
      <ServiceBeds v={v} />

      <SharedKit b={b} proposals={v.coordination.allocations} role={v.me.role} />

      <Workload v={v} />

      <Fold title="Review results" hint={`${b.dashboard.saved} saved, ${b.dashboard.harmed} harmed, ${b.dashboard.died} died`} testId="last-log">
        <div className="tally">
          <span>
            <b className="display">{b.dashboard.saved}</b> saved
          </span>
          <span>
            <b className="display">{b.dashboard.harmed}</b> harmed
          </span>
          <span>
            <b className="display">{b.dashboard.died}</b> died
          </span>
        </div>
        <p className="muted bd-note">
          {b.callsLeft} call{b.callsLeft === 1 ? '' : 's'} for help left. Time-Out {b.timeOutNow ? 'is on now' : b.timeOutUsed ? 'used' : 'not used yet'}.
        </p>
      </Fold>

      {layerActs.length > 0 && (
        <div className="row wrap">
          {layerActs.map((a) => (
            <button key={a.id} className="btn btn-small" disabled={!a.enabled} title={a.reason} onClick={() => act(a)} data-testid={`act-${a.id}`}>
              {a.label}
              {a.cost > 0 ? ` (${a.cost} action${a.cost > 1 ? 's' : ''})` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Keeps planned and crisis requests visible to the whole table. */
function Bookings({ bookings }: { bookings: BoardView['bookings'] }): ReactElement | null {
  if (bookings.length === 0) return null;
  return (
    <section className="bd-kit" aria-label="Booked resources">
      <h3 className="sec-title">Booked resources</h3>
      <div className="kit-grid">
        {bookings.map((b, i) => (
          <div className={`kit ${b.status === 'finished' ? 'used' : 'busy'}`} key={i} data-testid={`booking-${i}`}>
            <strong>{RESOURCE_TITLE[b.kind]}</strong>
            <span className="kit-detail">Turn {b.from}{b.until === null ? ' onward' : `–${b.until}`} · {bookingStatusLabel(b)}</span>
            <span className="kit-detail">{b.request === 'PLANNED' ? 'Planned' : 'Crisis call'} · {b.used} {b.kind === 'THEATRE' ? 'operations used' : b.kind === 'BED_MEETING' ? 'discharge actions saved' : 'actions used'}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** One plain sentence for a forecast row; the chances are rough, so the wording says 'about'. */
export function forecastText(row: BoardView['forecast'][number]): string {
  if (row.role !== 'EM' && row.estimate === 0) return 'No new patients expected from Emergency.';
  const n = `About ${row.estimate} new patient${row.estimate === 1 ? '' : 's'} for ${ROLE_TITLE[row.role]}`;
  if (row.tooHighPct === 0 && row.tooLowPct === 0) return `${n}, with little uncertainty.`;
  return `${n}; about ${row.tooHighPct}% chance this is too high and ${row.tooLowPct}% too low.`;
}

/** Keeps forecasts distinct from patients already on the table. */
function Forecast({ forecast }: { forecast: BoardView['forecast'] }): ReactElement | null {
  if (forecast.length === 0) return null;
  return (
    <section className="bd-kit" aria-label="Bed manager's forecast">
      <h3 className="sec-title">Bed manager’s forecast</h3>
      <div className="kit-grid">
        {forecast.map((row, i) => (
          <div className="kit" key={row.turn} data-testid={`forecast-${row.turn}`}>
            <span className="kit-name">{i > 0 ? 'Following turn' : 'Next turn'}: {row.hour} · turn {row.turn}</span>
            <span className="kit-detail">{forecastText(row)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BedMeter({ used, beds, corridor }: { used: number; beds: number; corridor: number }) {
  return (
    <span className="bedmeter" role="img" aria-label={`${used} of ${beds} beds used, ${corridor} without a bed`}>
      {Array.from({ length: beds }, (_, i) => (
        <i key={i} className={i < used ? 'on' : ''} />
      ))}
      {Array.from({ length: corridor }, (_, i) => (
        <i key={`c${i}`} className="cor" />
      ))}
    </span>
  );
}

function Kit({ name, state, detail, allocation }: { name: string; state: 'free' | 'used' | 'busy' | 'closed'; detail: string; allocation?: ReactElement }) {
  return (
    <div className={`kit ${state}`}>
      <span className="kit-name">{name}</span>
      <span className="kit-detail">{detail}</span>
      {allocation}
    </div>
  );
}

